import { useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, Plus, Download, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);

const parseQty = (it) => {
  const raw = it.qty_num;
  if (raw != null && raw !== "" && !Number.isNaN(Number(raw))) return Number(raw);
  const s = String(it.quantity || "").trim();
  const m = s.match(/^-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : 1;
};

const lineAmount = (it) => {
  const q = parseQty(it);
  const p = Number(it.price_inr || 0);
  return q * p;
};

const computeTotals = (items, discountType, discountValue) => {
  let subtotal = 0, cgst = 0, sgst = 0, igst = 0;
  (items || []).forEach((it) => {
    const incl = lineAmount(it);
    const c = Number(it.cgst_pct || 0);
    const s = Number(it.sgst_pct || 0);
    const i = Number(it.igst_pct || 0);
    const totalPct = c + s + i;
    const base = totalPct ? incl / (1 + totalPct / 100) : incl;
    subtotal += base;
    cgst += base * (c / 100);
    sgst += base * (s / 100);
    igst += base * (i / 100);
  });
  const gross = subtotal + cgst + sgst + igst;
  const dv = Number(discountValue || 0);
  const discountInr = discountType === "percentage" ? subtotal * (dv / 100) : dv;
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    cgst: Math.round(cgst * 100) / 100,
    sgst: Math.round(sgst * 100) / 100,
    igst: Math.round(igst * 100) / 100,
    discount: Math.round(discountInr * 100) / 100,
    total: Math.round((gross - discountInr) * 100) / 100,
  };
};

export default function ProjectInvoicesCard({ projectId }) {
  const [invoices, setInvoices] = useState([]);
  const [seed, setSeed] = useState(null); // { items, discount_inr, template_name } | null
  const [seedError, setSeedError] = useState(null);
  const [dialog, setDialog] = useState({ open: false, editing: null });

  const load = () => {
    api.get(`/projects/${projectId}/invoices`).then((r) => setInvoices(r.data || []));
    api.get(`/projects/${projectId}/confirmed-proposal`)
      .then((r) => { setSeed(r.data); setSeedError(null); })
      .catch((e) => { setSeed(null); setSeedError(e?.response?.data?.detail || "No confirmed proposal"); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const openPdf = (iid) => {
    const url = `${api.defaults.baseURL}/invoices/${iid}/pdf`;
    window.open(url, "_blank", "noopener");
  };

  const canCreate = !!seed;

  return (
    <div className="flat-card p-6" data-testid="project-invoices-card">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" /> Invoices
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={!canCreate}
          onClick={() => setDialog({ open: true, editing: null })}
          data-testid="invoice-create-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Create
        </Button>
      </div>

      {!canCreate && (
        <div className="text-xs text-[#9CA3AF]" data-testid="invoice-no-proposal">
          {seedError || "Confirm a proposal to enable invoicing."}
        </div>
      )}

      {invoices.length === 0 ? (
        canCreate && (
          <div className="text-xs text-[#9CA3AF]" data-testid="invoice-empty-state">
            No invoices generated for this project yet.
          </div>
        )
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <div
              key={inv.id}
              className="border border-[#E5E7EB] hover:border-[#002FA7] hover:bg-[#F9FAFB] px-3 py-2 transition-colors flex items-center justify-between gap-2"
              data-testid={`invoice-row-${inv.id}`}
            >
              <button
                type="button"
                onClick={() => openPdf(inv.id)}
                className="flex-1 text-left min-w-0"
              >
                <div className="font-mono text-xs font-bold text-[#0a0a0a] truncate">
                  {inv.invoice_no}
                </div>
                <div className="text-[10px] text-[#4B5563]">
                  {inv.invoice_date} · {(inv.items || []).length} items
                  {inv.discount_inr > 0 && <> · Discount {inr(inv.discount_inr)}</>}
                </div>
              </button>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs font-bold text-[#002FA7]">{inr(inv.total_inr || 0)}</span>
                <button
                  type="button"
                  onClick={() => setDialog({ open: true, editing: inv })}
                  className="text-[#4B5563] hover:text-[#002FA7]"
                  title="Edit invoice"
                  data-testid={`invoice-edit-${inv.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => openPdf(inv.id)}
                  className="text-[#4B5563] hover:text-[#002FA7]"
                  title="Download PDF"
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(canCreate || dialog.editing) && (
        <InvoiceDialog
          open={dialog.open}
          onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
          projectId={projectId}
          seed={seed}
          editing={dialog.editing}
          onSaved={(inv, action) => {
            setDialog({ open: false, editing: null });
            load();
            if (action === "created") {
              openPdf(inv.id);
              toast.success(`Invoice ${inv.invoice_no} created`);
            } else {
              toast.success(`Invoice ${inv.invoice_no} updated`);
            }
          }}
        />
      )}
    </div>
  );
}

function InvoiceDialog({ open, onOpenChange, projectId, seed, editing, onSaved }) {
  const isEdit = !!editing;
  const [invoiceNo, setInvoiceNo] = useState("");
  const [items, setItems] = useState([]);
  const [discountType, setDiscountType] = useState("fixed"); // fixed | percentage
  const [discountValue, setDiscountValue] = useState(0);
  const [notes, setNotes] = useState("");
  const [customerGstin, setCustomerGstin] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setInvoiceNo(editing.invoice_no || "");
      setItems((editing.items || []).map((it) => ({
        name: it.name || "", description: it.description || "",
        quantity: it.quantity || "1",
        qty_num: it.qty_num != null && it.qty_num !== "" ? Number(it.qty_num) : parseQty(it),
        price_inr: Number(it.price_inr || 0),
        cgst_pct: Number(it.cgst_pct || 0),
        sgst_pct: Number(it.sgst_pct || 0),
        igst_pct: Number(it.igst_pct || 0),
      })));
      setDiscountType(editing.discount_type || "fixed");
      setDiscountValue(Number(editing.discount_value || editing.discount_inr || 0));
      setNotes(editing.notes || "");
      setCustomerGstin(editing.customer_gstin || "");
      setInvoiceDate(editing.invoice_date || todayISO());
    } else {
      const seeded = (seed?.items || []).map((it) => ({
        name: it.name || "", description: it.description || "",
        quantity: it.quantity || "1",
        qty_num: it.qty_num != null && it.qty_num !== "" ? Number(it.qty_num) : parseQty(it),
        price_inr: Number(it.price_inr || 0),
        cgst_pct: Number(it.cgst_pct || 0),
        sgst_pct: Number(it.sgst_pct || 0),
        igst_pct: Number(it.igst_pct || 0),
      }));
      setInvoiceNo("");
      setItems(seeded);
      setDiscountType("fixed");
      setDiscountValue(Number(seed?.discount_inr || 0));
      setNotes("");
      setCustomerGstin("");
      setInvoiceDate(todayISO());
    }
  }, [open, seed, editing, isEdit]);

  const totals = useMemo(() => computeTotals(items, discountType, discountValue), [items, discountType, discountValue]);

  const patchItem = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const addBlankItem = () => setItems([
    ...items,
    { name: "", description: "", quantity: "1", qty_num: 1, price_inr: 0, cgst_pct: 0, sgst_pct: 0, igst_pct: 0 },
  ]);

  const submit = async () => {
    if (!items.length) { toast.error("Add at least one line item"); return; }
    if (items.some((it) => !it.name?.trim())) { toast.error("Every line item needs a name"); return; }
    setBusy(true);
    try {
      const payload = {
        invoice_no: invoiceNo.trim() || null,
        invoice_date: invoiceDate || todayISO(),
        discount_type: discountType,
        discount_value: Number(discountValue || 0),
        notes: notes?.trim() || null,
        customer_gstin: customerGstin.trim(),
        items: items.map((it) => ({
          name: it.name,
          description: it.description || null,
          quantity: String(it.quantity ?? "1"),
          qty_num: Number(it.qty_num || 0),
          price_inr: Number(it.price_inr || 0),
          cgst_pct: Number(it.cgst_pct || 0),
          sgst_pct: Number(it.sgst_pct || 0),
          igst_pct: Number(it.igst_pct || 0),
        })),
      };
      let data;
      if (isEdit) {
        const r = await api.patch(`/invoices/${editing.id}`, payload);
        data = r.data;
        onSaved(data, "updated");
      } else {
        const r = await api.post("/invoices", { project_id: projectId, ...payload });
        data = r.data;
        onSaved(data, "created");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${isEdit ? "update" : "create"} invoice`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="invoice-create-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Invoice ${editing.invoice_no}` : "Create Invoice from Confirmed Quotation"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="label-eyebrow">Invoice No.</Label>
            <Input
              value={invoiceNo}
              onChange={(e) => setInvoiceNo(e.target.value)}
              placeholder={isEdit ? "" : "Leave blank → auto-numbered"}
              data-testid="invoice-no-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Invoice Date</Label>
            <Input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              data-testid="invoice-date-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Source Template</Label>
            <div className="text-sm py-2">{seed?.template_name || "—"} · Option {seed?.confirmed_option || 1}</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="label-eyebrow">Customer GSTIN (optional)</Label>
            <Input
              value={customerGstin}
              onChange={(e) => setCustomerGstin(e.target.value.toUpperCase())}
              placeholder="e.g. 32ABCDE1234F1Z5"
              maxLength={15}
              data-testid="invoice-customer-gstin-input"
            />
          </div>
        </div>

        <div className="border border-[#E5E7EB]">
          <table className="w-full text-xs">
            <thead className="bg-[#F9FAFB] text-[#6B7280]">
              <tr>
                <th className="px-2 py-2 text-left">Item</th>
                <th className="px-2 py-2 text-right w-16">Qty</th>
                <th className="px-2 py-2 text-right w-28">Unit ₹ (incl. GST)</th>
                <th className="px-2 py-2 text-right w-16">CGST %</th>
                <th className="px-2 py-2 text-right w-16">SGST %</th>
                <th className="px-2 py-2 text-right w-16">IGST %</th>
                <th className="px-2 py-2 text-right w-24">Amount</th>
                <th className="px-2 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t border-[#F3F4F6] align-top" data-testid={`invoice-item-row-${i}`}>
                  <td className="px-2 py-2">
                    <Input
                      value={it.name}
                      onChange={(e) => patchItem(i, { name: e.target.value })}
                      placeholder="Item name"
                      data-testid={`invoice-item-name-${i}`}
                    />
                    <Textarea
                      value={it.description || ""}
                      onChange={(e) => patchItem(i, { description: e.target.value })}
                      placeholder="Description (optional)"
                      className="mt-1 min-h-[36px] text-xs"
                      rows={1}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      step="any"
                      value={it.qty_num}
                      onChange={(e) => patchItem(i, { qty_num: e.target.value, quantity: String(e.target.value) })}
                      className="text-right"
                      data-testid={`invoice-item-qty-${i}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      step="any"
                      value={it.price_inr}
                      onChange={(e) => patchItem(i, { price_inr: e.target.value })}
                      className="text-right"
                      data-testid={`invoice-item-price-${i}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      step="any"
                      value={it.cgst_pct}
                      onChange={(e) => patchItem(i, { cgst_pct: e.target.value })}
                      className="text-right"
                      data-testid={`invoice-item-cgst-${i}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      step="any"
                      value={it.sgst_pct}
                      onChange={(e) => patchItem(i, { sgst_pct: e.target.value })}
                      className="text-right"
                      data-testid={`invoice-item-sgst-${i}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      step="any"
                      value={it.igst_pct}
                      onChange={(e) => patchItem(i, { igst_pct: e.target.value })}
                      className="text-right"
                      data-testid={`invoice-item-igst-${i}`}
                    />
                  </td>
                  <td className="px-2 py-2 text-right font-mono">{inr(lineAmount(it))}</td>
                  <td className="px-2 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeItem(i)}
                      className="text-[#991B1B] hover:text-red-800"
                      data-testid={`invoice-item-remove-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-2 py-2 border-t border-[#F3F4F6]">
            <Button size="sm" variant="ghost" onClick={addBlankItem} data-testid="invoice-add-item-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mt-4">
          <div>
            <Label className="label-eyebrow">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Payment terms, delivery notes, etc."
              rows={4}
              data-testid="invoice-notes-input"
            />
          </div>
          <div className="text-xs">
            <div className="flex justify-between py-1">
              <span>Subtotal (pre-tax)</span>
              <span className="font-mono" data-testid="invoice-subtotal">{inr(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between py-1"><span>CGST</span><span className="font-mono">{inr(totals.cgst)}</span></div>
            <div className="flex justify-between py-1"><span>SGST</span><span className="font-mono">{inr(totals.sgst)}</span></div>
            <div className="flex justify-between py-1"><span>IGST</span><span className="font-mono">{inr(totals.igst)}</span></div>
            <div className="flex justify-between items-center py-1 gap-2">
              <span>Discount</span>
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger className="w-24 h-8 text-xs" data-testid="invoice-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">₹</SelectItem>
                  <SelectItem value="percentage">%</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                step="any"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                className="w-24 text-right"
                data-testid="invoice-discount-input"
              />
            </div>
            {totals.discount > 0 && (
              <div className="flex justify-between py-1 text-[11px] text-[#4B5563]">
                <span>Discount applied</span>
                <span className="font-mono">− {inr(totals.discount)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 border-t border-[#0a0a0a] mt-2 text-sm font-bold text-[#002FA7]">
              <span>Grand Total</span>
              <span className="font-mono" data-testid="invoice-grand-total">{inr(totals.total)}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="invoice-cancel-btn">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="invoice-submit-btn">
            {busy ? (isEdit ? "Saving…" : "Generating…") : (isEdit ? "Save Changes" : "Generate Invoice PDF")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
